import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  ORDER_EMAIL_TEMPLATES,
  emailTemplateLabel,
  sendOrderEmail,
  statusToEmailKey,
  type OrderEmailStatusKey,
} from '../../lib/orders';

interface SendEmailDialogProps {
  orderId: string;
  orderNumber: string;
  currentStatus: string;
  isOpen: boolean;
  onClose: () => void;
  onSent?: (templateUsed: OrderEmailStatusKey) => void;
}

const PRIMARY_S01: OrderEmailStatusKey = 'S01_OpdrachtBevestiging';

export function SendEmailDialog({
  orderId,
  orderNumber,
  currentStatus,
  isOpen,
  onClose,
  onSent,
}: SendEmailDialogProps) {
  const statusEmailKey = useMemo(() => statusToEmailKey(currentStatus), [currentStatus]);

  const [selected, setSelected] = useState<OrderEmailStatusKey | null>(null);
  const [showOther, setShowOther] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const otherTemplates = ORDER_EMAIL_TEMPLATES.filter(
    (t) => t.key !== PRIMARY_S01 && t.key !== statusEmailKey
  );

  const handleSend = async (key: OrderEmailStatusKey) => {
    setSelected(key);
    setSubmitting(true);
    setError(null);
    try {
      const result = await sendOrderEmail(orderId, key);
      onSent?.(result.templateUsed);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'E-mail versturen mislukt';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">E-mail versturen</h2>
          <p className="text-xs text-slate-500 mt-1">Ordernummer: {orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-xs text-slate-600">
            Verstuur een transactionele e-mail naar de klant. De e-mail komt op
            de Activity-timeline van de order te staan.
          </p>

          {/* Primaire knoppen */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => handleSend(PRIMARY_S01)}
              disabled={submitting}
              className={clsx(
                'w-full text-left px-3 py-2 rounded border text-sm transition-colors',
                submitting && selected === PRIMARY_S01
                  ? 'bg-slate-100 border-slate-300 text-slate-500 cursor-wait'
                  : 'bg-indigo-50 border-indigo-200 text-indigo-900 hover:bg-indigo-100'
              )}
            >
              <div className="font-medium">Bevestiging opnieuw (S01)</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Stuur de orderbevestiging incl. wijzigingen
              </div>
            </button>

            {statusEmailKey && (
              <button
                type="button"
                onClick={() => handleSend(statusEmailKey)}
                disabled={submitting}
                className={clsx(
                  'w-full text-left px-3 py-2 rounded border text-sm transition-colors',
                  submitting && selected === statusEmailKey
                    ? 'bg-slate-100 border-slate-300 text-slate-500 cursor-wait'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100'
                )}
              >
                <div className="font-medium">{emailTemplateLabel(statusEmailKey)}</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Status-mail voor "{currentStatus}"
                </div>
              </button>
            )}
          </div>

          {/* Andere templates */}
          <div className="pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setShowOther((v) => !v)}
              className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
              disabled={submitting}
            >
              <svg
                className={clsx('w-3 h-3 transition-transform', showOther && 'rotate-90')}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Andere e-mails ({otherTemplates.length})
            </button>

            {showOther && (
              <div className="mt-2 space-y-1 max-h-56 overflow-y-auto">
                {otherTemplates.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => handleSend(t.key)}
                    disabled={submitting}
                    className={clsx(
                      'w-full text-left px-2 py-1.5 rounded text-xs transition-colors',
                      submitting && selected === t.key
                        ? 'bg-slate-100 text-slate-500 cursor-wait'
                        : 'text-slate-700 hover:bg-slate-100'
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 disabled:opacity-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
